import { SPREAD_DIMENSIONS } from '../constants';

export function getBounds(containerRef, elementSize = null, elementType = null) {
  // If element type is 'image', we'll allow it to be dragged beyond the bleed area
  const allowExtendBeyondBleed = elementType === 'image';
  
  if (!containerRef?.current) {
    // Default bounds based on spread dimensions
    const { width, height, safeZone } = SPREAD_DIMENSIONS;
    
    // For images, we extend the bounds to allow dragging beyond the bleed area
    const minX = allowExtendBeyondBleed ? -elementSize?.width / 2 : safeZone;
    const minY = allowExtendBeyondBleed ? -elementSize?.height / 2 : safeZone;
    const maxX = allowExtendBeyondBleed 
      ? width * 2 + elementSize?.width / 2
      : width * 2 - (elementSize?.width || 0) - safeZone;
    const maxY = allowExtendBeyondBleed
      ? height + elementSize?.height / 2
      : height - (elementSize?.height || 0) - safeZone;
    
    return { minX, minY, maxX, maxY };
  }

  const rect = containerRef.current.getBoundingClientRect();
  const padding = allowExtendBeyondBleed ? 0 : 16; // Remove padding constraints for images

  // For images, allow dragging beyond container bounds
  const minX = allowExtendBeyondBleed ? -elementSize?.width / 2 : padding;
  const minY = allowExtendBeyondBleed ? -elementSize?.height / 2 : padding;
  const maxX = allowExtendBeyondBleed
    ? rect.width + elementSize?.width / 2
    : rect.width - padding - (elementSize?.width || 0);
  const maxY = allowExtendBeyondBleed
    ? rect.height + elementSize?.height / 2
    : rect.height - padding - (elementSize?.height || 0);

  return { minX, minY, maxX, maxY };
}

export function clampPosition(position, bounds = null, elementType = null) {
  if (!bounds) {
    bounds = getBounds(null, null, elementType);
  }

  // For text elements, allow free horizontal movement
  if (elementType === 'text') {
    return {
      x: position.x, // Don't clamp x position for text
      y: Math.max(bounds.minY, Math.min(bounds.maxY, position.y))
    };
  }

  return {
    x: Math.max(bounds.minX, Math.min(bounds.maxX, position.x)),
    y: Math.max(bounds.minY, Math.min(bounds.maxY, position.y))
  };
}

export function clampSize(size, minSize = { width: 20, height: 20 }, aspectRatio = null, previousSize = null) {
  let newSize = {
    width: Math.max(minSize.width, size.width),
    height: Math.max(minSize.height, size.height)
  };

  // If aspect ratio is provided, apply orientation-aware constraint
  if (aspectRatio && aspectRatio > 0) {
    // Landscape images (aspectRatio > 1): width is primary, height adjusts
    // Portrait images (aspectRatio < 1): height is primary, width adjusts
    const isLandscape = aspectRatio > 1;
    
    if (isLandscape) {
      // For landscape: constrain height based on width
      newSize.height = Math.round(newSize.width / aspectRatio);
      if (newSize.height < minSize.height) {
        newSize.height = minSize.height;
        newSize.width = Math.round(newSize.height * aspectRatio);
      }
    } else {
      // For portrait: constrain width based on height
      newSize.width = Math.round(newSize.height * aspectRatio);
      if (newSize.width < minSize.width) {
        newSize.width = minSize.width;
        newSize.height = Math.round(newSize.width / aspectRatio);
      }
    }
  }

  return newSize;
}